import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Dialog as D } from "radix-ui";
import { CornerDownLeft, Search } from "lucide-react";
import { cn } from "../../lib/cn";

export interface Command { id: string; label: string; group: string; to?: string; run?: () => void; keywords?: string }

/** ⌘K / Ctrl+K palette with fuzzy-ish filtering and full keyboard control. */
export function CommandPalette({ commands, open: openProp, onOpenChange, trigger = true }: { commands: Command[]; open?: boolean; onOpenChange?: (open: boolean) => void; trigger?: boolean }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const openRef = useRef(open);
  openRef.current = open;
  const setOpen = (v: boolean | ((o: boolean) => boolean)) => {
    const next = typeof v === "function" ? v(openRef.current) : v;
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const nav = useNavigate();
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => {
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    return commands.filter((c) => terms.every((t) => `${c.label} ${c.group} ${c.keywords ?? ""}`.toLowerCase().includes(t))).slice(0, 30);
  }, [q, commands]);

  useEffect(() => setIdx(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${idx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  const exec = (c: Command | undefined) => {
    if (!c) return;
    setOpen(false);
    setQ("");
    if (c.run) c.run();
    else if (c.to) nav(c.to);
  };

  return (
    <>
      {trigger && <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden h-8 w-64 items-center gap-2 rounded-md border border-border bg-bg-elev px-2.5 text-xs text-fg-3 hover:border-border-strong md:flex"
      >
        <Search className="size-3.5" aria-hidden /> Search or jump to…
        <kbd className="ml-auto rounded border border-border px-1 font-mono text-[10px]">⌘K</kbd>
      </button>}
      <D.Root open={open} onOpenChange={setOpen}>
        <D.Portal>
          <D.Overlay className="fixed inset-0 z-50 bg-black/50" />
          <D.Content className="fixed left-1/2 top-[12vh] z-50 w-[min(600px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-lg border border-border-strong bg-panel shadow-2xl">
            <D.Title className="sr-only">Command palette</D.Title>
            <D.Description className="sr-only">Type to filter, arrow keys to move, Enter to open.</D.Description>
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search className="size-4 text-fg-3" aria-hidden />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") (e.preventDefault(), setIdx((i) => Math.min(results.length - 1, i + 1)));
                  else if (e.key === "ArrowUp") (e.preventDefault(), setIdx((i) => Math.max(0, i - 1)));
                  else if (e.key === "Enter") (e.preventDefault(), exec(results[idx]));
                }}
                placeholder="Search pages and actions…"
                className="h-11 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-3"
                role="combobox"
                aria-expanded="true"
                aria-controls="cmdk-list"
                aria-activedescendant={results[idx] ? `cmdk-${results[idx]!.id}` : undefined}
              />
            </div>
            <ul ref={listRef} id="cmdk-list" role="listbox" className="max-h-[50vh] overflow-y-auto p-1.5">
              {results.length === 0 && <li className="px-3 py-6 text-center text-sm text-fg-3">No matches</li>}
              {results.map((c, i) => (
                <li
                  key={c.id}
                  id={`cmdk-${c.id}`}
                  data-idx={i}
                  role="option"
                  aria-selected={i === idx}
                  onMouseMove={() => setIdx(i)}
                  onClick={() => exec(c)}
                  className={cn("flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm", i === idx ? "bg-panel-2 text-fg" : "text-fg-2")}
                >
                  <span>
                    {c.label}
                    <span className="ml-2 text-xs text-fg-3">{c.group}</span>
                  </span>
                  {i === idx && <CornerDownLeft className="size-3.5 text-fg-3" aria-hidden />}
                </li>
              ))}
            </ul>
          </D.Content>
        </D.Portal>
      </D.Root>
    </>
  );
}
